import graphqlResource, { GraphqlResource } from 'modules/core/graphql-resource';
import { mapTeamDtoToTeam } from 'modules/games/domain/data-mappers/game.data-mapper';
import { TeamGroup } from '../interfaces/team-group';
import { TeamGroupStatisticsRowDto } from '../interfaces/team-group-statistics-dto';

/** Группы команд внутри этапа. */

export interface UpsertStageTeamGroupParams {
  id?: number;
  name: string;
  stageId: number;
  teamIds: number[];
  sortOrder?: number | null;
}

interface UpsertStageTeamGroupResponse {
  data: {
    upsertTeamGroup: {
      id: number;
      name: string;
      stage: { id: number };
      teams: Array<{
        id: number;
        name: string;
        shortName: string;
        emblem?: {
          id: number;
          externalUrl: string;
        };
      }>;
      groupChampionshipTable: {
        rows: TeamGroupStatisticsRowDto[];
      };
      sortOrder: number | null;
      createdAt: string;
      updatedAt: string;
    };
  };
}

interface DeleteStageTeamGroupResponse {
  data: {
    deleteTeamGroup: {
      status: string;
    };
  };
}

class TeamGroupRepository {
  constructor(private readonly graphqlResource: GraphqlResource) {}

  public async upsertStageTeamGroup(params: UpsertStageTeamGroupParams): Promise<TeamGroup> {
    const query = `#graphql
      mutation upsertTeamGroup($input: UpsertTeamGroupInput!) {
        upsertTeamGroup(input: $input) {
          id
          name
          stage { id }
          teams {
            id
            name
            shortName
            emblem {
              id
              externalUrl
            }
          }
          groupChampionshipTable {
            rows {
              position
              team { id }
              gamesPlayed
              gamesWon
              gamesDraw
              gamesLost
              goalsWon
              goalsLost
              championshipScore
            }
          }
          sortOrder
          createdAt
          updatedAt
        }
      }
    `;

    const variables = { input: params };

    const response = await this.graphqlResource.query<UpsertStageTeamGroupResponse>(query, variables);
    const result = response.data.data.upsertTeamGroup;

    const statistics = result.groupChampionshipTable.rows.map((row) => ({
      position: row.position,
      teamId: row.team.id,
      gamesPlayed: row.gamesPlayed,
      gamesWon: row.gamesWon,
      gamesLost: row.gamesLost,
      gamesDraw: row.gamesDraw,
      goalsWon: row.goalsWon,
      goalsLost: row.goalsLost,
      score: row.championshipScore,
    }));

    return {
      id: result.id,
      name: result.name,
      stageId: result.stage.id,
      teams: result.teams.map((team) => mapTeamDtoToTeam(team, null)),
      statistics,
      sortOrder: result.sortOrder ?? null,
      createdAt: result.createdAt,
      updatedAt: result.updatedAt,
    };
  }

  public async deleteStageTeamGroup(teamGroupId: number): Promise<boolean> {
    const query = `#graphql
      mutation deleteTeamGroup($id: Int!) {
        deleteTeamGroup(id: $id) {
          status
        }
      }
    `;

    const response = await this.graphqlResource.query<DeleteStageTeamGroupResponse>(query, { id: teamGroupId });
    const { status } = response.data.data.deleteTeamGroup;

    return status === 'ok';
  }
}

const teamGroupRepository = new TeamGroupRepository(graphqlResource);

export default teamGroupRepository;
